import axios from '../utils/axios';
import type {
  MetrcFacilityDocument,
  MetrcProductDocument,
} from '../types/index';
import {
  createLegacyResultError,
  createRequestError,
  getErrorStatus,
} from '../utils/requestErrors';

function normalizeFacilityDocuments(responseData: any): MetrcFacilityDocument[] {
  const documents = Array.isArray(responseData)
    ? responseData
    : (Array.isArray(responseData?.documents) ? responseData.documents : []);

  return documents
    .map((facility) => ({
      id: Number(facility?.id || 0),
      dbFacilityId: facility?.dbFacilityId ?? facility?.dbFacilityID ?? null,
      name: String(facility?.name || ''),
      license: String(facility?.license || ''),
    }))
    .filter((facility) => facility.id || facility.license);
}

function normalizeProductDocuments(responseData: any): MetrcProductDocument[] {
  const documents = Array.isArray(responseData)
    ? responseData
    : (Array.isArray(responseData?.documents) ? responseData.documents : []);

  return documents.map((product) => ({
    ...product,
    id: Number(product?.id || 0),
    name: String(product?.name || ''),
    dbProduct: product?.dbProduct || null,
  }));
}

export async function loginToMarket(payload: {
  marketId: number;
  username: string;
  password: string;
}): Promise<any> {
  try {
    const response = await axios.post('/market', payload);
    return response.data;
  } catch (error) {
    const status = getErrorStatus(error);
    if (status === 400 || status === 422 || status === 502) {
      return createLegacyResultError(error);
    }
    throw createRequestError(error);
  }
}

export async function getAvailableFacilities(payload: {
  organizationId: number | string;
  marketId: number;
  page?: number;
  pageSize?: number;
}): Promise<MetrcFacilityDocument[]> {
  try {
    const response = await axios.get('/facility/available', {
      params: payload,
    });
    return normalizeFacilityDocuments(response.data);
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function importFacility(payload: {
  organizationId: number | string;
  marketId: number;
  metrcFacilityId: number;
}): Promise<any> {
  try {
    const response = await axios.post('/facility/import', payload);
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}

export async function getAvailableProducts(payload: {
  organizationId?: number;
  marketId: number;
  metrcFacilityId: number;
  page?: number;
  pageSize?: number;
  metrcPackageIds?: number[] | null;
}): Promise<MetrcProductDocument[]> {
  try {
    const response = await axios.post('/product/available', payload);
    return normalizeProductDocuments(response.data);
  } catch (error) {
    const status = getErrorStatus(error);
    if (status === 400 || status === 502) {
      const legacyError = createLegacyResultError(error);
      throw Object.assign(new Error('Request failed'), legacyError);
    }
    throw createRequestError(error);
  }
}

export async function importProduct(payload: {
  organizationId: number | string;
  metrcProductId: number;
  facilityId: number | string | null | undefined;
  productId?: number | string | null;
}): Promise<any> {
  try {
    const response = await axios.post('/product/import', payload);
    return response.data;
  } catch (error) {
    throw createRequestError(error);
  }
}
